/**
 * TrackedListsPanel.jsx — pick which BigDough list names get Follow-up
 * tracking on the Calendar, and set each one's own day-by-day sequence,
 * instead of hand-writing a wildcard pattern + comma-separated offsets in
 * Settings. Checking a list name here always creates (or updates) a
 * *dedicated* rule for that exact name — see cadence_stage_rules in
 * Settings/backend/cadence.js — inserted at the front of the rules list so
 * it takes priority over any broader wildcard rule that might also match
 * it (that broader rule, and everything else it covers, is untouched).
 *
 * List names shown here are cadence *groups* (e.g. "G1" — already folded
 * from raw upload strings like "Generalist G1 0701" by the backend), read
 * straight from /api/cadence's timelines so this always reflects every
 * name that's actually been uploaded, tracked or not.
 */
import { useState, useEffect, useCallback } from 'react';
import { fetchCadence, fetchConfig, updateConfig } from '../api/backend.js';

function parseRules(rulesJson) {
  if (!rulesJson) return [];
  try {
    const parsed = JSON.parse(rulesJson);
    return Array.isArray(parsed)
      ? parsed.map((r) => ({ pattern: String(r?.pattern ?? ''), offsets: String(r?.offsets ?? '') })).filter((r) => r.pattern)
      : [];
  } catch {
    return [];
  }
}

// "2,6,14" (the days-until-each-follow-up a person actually thinks in) <->
// "0,2,6,14" (the stored offsets array, stage 0 = First Send = always 0).
function followUpDaysToOffsets(days) {
  return ['0', ...days.map((d) => String(d))].join(',');
}
function offsetsToFollowUpDays(offsetsStr) {
  const parts = String(offsetsStr ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  return parts.slice(1); // drop the implicit stage-0 "0"
}

export default function TrackedListsPanel({ getToken }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]); // [{ listName, isTrackedCadence }]
  const [rules, setRules] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [draftDays, setDraftDays] = useState(['2']);
  const [advanced, setAdvanced] = useState(false);
  const [draftPattern, setDraftPattern] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!getToken) return;
    setLoading(true);
    setError('');
    try {
      const [cadence, cfg] = await Promise.all([fetchCadence(getToken), fetchConfig(getToken)]);
      const seen = new Map();
      for (const t of cadence.timelines ?? []) {
        if (t.predicted) continue; // future-only placeholder, not a real uploaded list yet
        if (!seen.has(t.listName)) seen.set(t.listName, !!t.isTrackedCadence);
      }
      setGroups([...seen.entries()].map(([listName, isTrackedCadence]) => ({ listName, isTrackedCadence })).sort((a, b) => a.listName.localeCompare(b.listName)));
      setRules(parseRules(cfg.cadence_stage_rules));
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { if (expanded) load(); }, [expanded, load]);

  function startEditing(listName) {
    const existing = rules.find((r) => r.pattern === listName);
    setDraftDays(existing ? offsetsToFollowUpDays(existing.offsets) : ['2']);
    setDraftPattern(listName);
    setAdvanced(false);
    setEditingKey(listName);
  }

  async function persistRules(nextRules) {
    setSaving(true);
    setError('');
    try {
      setRules(nextRules);
      await updateConfig(getToken, { cadence_stage_rules: JSON.stringify(nextRules) });
      await load();
      setEditingKey(null);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function handleSaveSequence() {
    const days = draftDays.map((d) => Number(d)).filter((n) => Number.isFinite(n) && n >= 0);
    if (!days.length) { setError('Enter at least one Follow-up day count.'); return; }
    const pattern = (advanced ? draftPattern : editingKey).trim();
    if (!pattern) { setError('Pattern cannot be blank.'); return; }
    const withoutOld = rules.filter((r) => r.pattern !== editingKey && r.pattern !== pattern);
    persistRules([{ pattern, offsets: followUpDaysToOffsets(days) }, ...withoutOld]);
  }

  function handleUntrack(listName) {
    persistRules(rules.filter((r) => r.pattern !== listName));
  }

  return (
    <div className="bd-cadence-backfill" style={{ marginBottom: 16 }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="btn btn--ghost btn--sm"
        style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}
      >
        <span>📋 Tracked Lists (which get Follow-up tracking on the Calendar)</span>
        <span>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div style={{ marginTop: 8, padding: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)' }}>
          {loading && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>}
          {error && <div style={{ fontSize: 12.5, color: '#dc2626', marginBottom: 8 }}>{error}</div>}
          {!loading && !groups.length && (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No uploaded list names yet — upload a BigDough file with a list name to see it here.</div>
          )}

          {groups.map((g) => {
            const dedicated = rules.find((r) => r.pattern === g.listName);
            const isEditing = editingKey === g.listName;
            return (
              <div key={g.listName} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!dedicated}
                    onChange={(e) => (e.target.checked ? startEditing(g.listName) : handleUntrack(g.listName))}
                    disabled={saving}
                  />
                  <span style={{ fontWeight: 700 }}>{g.listName}</span>
                  {!dedicated && g.isTrackedCadence && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>— tracked via a broader rule; check to give it its own sequence</span>
                  )}
                  {dedicated && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      — Follow-up {offsetsToFollowUpDays(dedicated.offsets).join('d, ')}d after First Send
                    </span>
                  )}
                </label>

                {isEditing && (
                  <div style={{ marginTop: 8, marginLeft: 24, padding: 10, background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>Days after First Send for each Follow-up:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                      {draftDays.map((d, i) => (
                        <label key={i} style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                          Follow-up {i + 1}
                          <input
                            type="number" min={0} value={d}
                            onChange={(e) => setDraftDays((arr) => arr.map((v, idx) => (idx === i ? e.target.value : v)))}
                            style={{ display: 'block', width: 70, marginTop: 2, padding: '4px 6px', borderRadius: 5, border: '1px solid var(--border)', fontSize: 12.5 }}
                          />
                        </label>
                      ))}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="btn btn--ghost btn--sm" onClick={() => setDraftDays((arr) => [...arr, ''])}>+ Add stage</button>
                        {draftDays.length > 1 && (
                          <button className="btn btn--ghost btn--sm" onClick={() => setDraftDays((arr) => arr.slice(0, -1))}>Remove last</button>
                        )}
                      </div>
                    </div>

                    <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 8 }}>
                      <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} style={{ marginRight: 5 }} />
                      Advanced: use a wildcard pattern instead of matching only "{g.listName}" exactly
                    </label>
                    {advanced && (
                      <input
                        type="text" value={draftPattern} onChange={(e) => setDraftPattern(e.target.value)}
                        placeholder="e.g. G* to match every G-number"
                        style={{ display: 'block', width: '100%', marginBottom: 8, padding: '5px 7px', borderRadius: 5, border: '1px solid var(--border)', fontSize: 12.5 }}
                      />
                    )}

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn--primary btn--sm" onClick={handleSaveSequence} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn--ghost btn--sm" onClick={() => setEditingKey(null)} disabled={saving}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
